import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useArchivedCli, useUnarchiveCli, useUserArchivedCli } from '../api';
import type { ArchivedCliRow } from '../types';

/**
 * Used by /archived-cli (sub/super admin) and /user-archive-cli-list (USER).
 * The data source and unarchive `allocated` flag both flip on role.
 */
export function ArchivedCliListPage() {
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const sadminQuery = useArchivedCli();
  const userQuery = useUserArchivedCli();
   const query = isSubOrSuper ? sadminQuery : userQuery;
  const canViewArchive = usePermission('Manage CLI.Archived CLI List', BASE_PERMISSION.VIEW);

  const unarchive = useUnarchiveCli();

  if (!canViewArchive) return <Navigate to="/cli-api-list" replace />;

  if (isSubOrSuper && !canViewArchive) return <Navigate to="/cli-list" replace />;

  
  const [pending, setPending] = useState<ArchivedCliRow | null>(null);

  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<ArchivedCliRow>[]>(
    () => [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
      {
        id: 'actions',
        header: 'Settings',
        size: 150,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={() => setPending(row.original)}>
            <ArchiveRestore className="h-4 w-4" />
            Unarchive
          </Button>
        ),
      },
    ],
    [],
  );

  const confirmUnarchive = () => {
    if (!pending) return;
    unarchive.mutate(
      {
        cliId: pending.cliId,
        allocated: isSubOrSuper ? 1 : 2,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Unarchived ${pending.cli}`);
          setPending(null);
        },
        onError: () => toast.error('Could not unarchive the CLI.'),
      },
    );
  };

  if (!canViewArchive) return <Navigate to="/cli-list" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Archive"
        description="CLIs that have been archived. Restore them to make them assignable again."
      />

      <DataTable<ArchivedCliRow, unknown>
        tableId="cli:archived:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search archived CLIs…"
        emptyTitle="No archived CLIs"
        emptyDescription="Nothing has been archived yet."
        getRowId={(row) => String(row.cliId)}
        exportFilename="archived-cli.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Unarchive this CLI?"
        description={
          <>
            <span className="font-medium text-foreground">{pending?.cli}</span> will become
            available again for allocation.
          </>
        }
        confirmLabel="Unarchive"
        loading={unarchive.isPending}
        onConfirm={confirmUnarchive}
      />
    </FillPage>
  );
}
